// src/components/flight-data/BinAccordion.js
import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatConnectionTime, getCategoryStyle, isIcelandAirFlight } from '../../utils/connectionUtils';

function BinAccordion({ binName, binObj, isExpanded, onToggle, params, inboundETA }) {
  const [hoveredFlight, setHoveredFlight] = React.useState(null);

  // Destructure bin data
  const { flights = [], terminating = {}, total = {} } = binObj;
  const isEmpty = flights.length === 0 && (terminating.totalBags || 0) === 0;

  // If you have special logic for certain flights, you could handle it here:
  // e.g. const [specialFreight, setSpecialFreight] = React.useState({});
  // but we'll keep it empty for now.

  // 1) Merge duplicates in the front end
  const groupedFlights = useMemo(() => {
    // flightMap: key=flightNumber, value= merged flight
    const flightMap = new Map();

    flights.forEach(f => {
      const key = f.flightNumber || 'UNKNOWN';
      
      // If we already have that flightNumber, unify the data
      if (flightMap.has(key)) {
        const existing = flightMap.get(key);

        // Sum bags and freight
        existing.bags += (f.bags || 0);
        existing.freight += (f.freight || 0);

        // Keep per-destination amounts so each row can show its own
        // share instead of the merged flight total.
        const destKey = f.dest || '';
        if (!existing.destAmounts[destKey]) {
          existing.destAmounts[destKey] = { bags: 0, freight: 0 };
        }
        existing.destAmounts[destKey].bags += (f.bags || 0);
        existing.destAmounts[destKey].freight += (f.freight || 0);

        // If you want to unify destinations, check if it's different:
        if (f.dest && !existing.destinations.includes(f.dest)) {
          existing.destinations.push(f.dest);
          existing.isMultiDestination = true;
          existing.totalDestinations = existing.destinations.length;
        }
      } else {
        // Create a fresh entry
        const newObj = {
          ...f,
          // Ensure we have an array of destinations
          destinations: f.dest ? [f.dest] : [],
          destAmounts: { [f.dest || '']: { bags: f.bags || 0, freight: f.freight || 0 } },
          isMultiDestination: false,
          totalDestinations: 1
        };
        flightMap.set(key, newObj);
      }
    });

    // 2) Flatten the map to an array, expanding multi-dest flights if you want sub-rows
    const result = [];
    flightMap.forEach(fObj => {
      if (fObj.isMultiDestination) {
        // Each destination row shows its own bags/freight share,
        // not the merged flight total.
        const amountsFor = (dest) =>
          fObj.destAmounts[dest || ''] || { bags: 0, freight: 0 };

        // create one row for the first dest
        const firstDest = fObj.destinations[0];
        const firstRow = {
          ...fObj,
          isFirstDestination: true,
          dest: firstDest,
          bags: amountsFor(firstDest).bags,
          freight: amountsFor(firstDest).freight
        };
        result.push(firstRow);

        // sub-rows for subsequent destinations
        for (let i = 1; i < fObj.destinations.length; i++) {
          const dest = fObj.destinations[i];
          result.push({
            ...fObj,
            isFirstDestination: false,
            dest,
            bags: amountsFor(dest).bags,
            freight: amountsFor(dest).freight
          });
        }
      } else {
        // single-dest flight
        result.push(fObj);
      }
    });

    return result;
  }, [flights]);

  // For row hover highlight
  const getHoverColor = (category) => {
    if (category === 'hot')      return 'rgba(255, 0, 0, 0.6)';
    if (category === 'priority') return 'rgba(255, 165, 0, 0.5)';
    if (category === 'cold')     return 'rgba(0, 0, 255, 0.35)';
    return 'rgba(255, 255, 255, 0.1)'; 
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onToggle}
      sx={{ 
        backgroundColor: 'transparent',
        marginBottom: '0.5rem',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#FF0000' }} />}
        sx={{ backgroundColor: 'rgba(255, 0, 0, 0.1)' }}
      >
        <Typography sx={{ color: '#FF0000', fontWeight: 'bold' }}>
          Bin {binName} {isEmpty ? "(Empty)" : ""}
          {!isEmpty && (
            <Box component="span" sx={{ ml: 2 }}>
              Total: {total.totalBags || 0} | Terminating: {terminating.totalBags || 0}
            </Box>
          )}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {!isEmpty && (
          <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#fff' }}>Flight #</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Gate</TableCell>
                  <TableCell sx={{ color: '#fff' }}>ETD</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Connection</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Destination</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Bags</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Freight</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {groupedFlights.map((flight, idx) => {
                  const baseStyle = getCategoryStyle(flight.connectionCategory) || {};
                  const isHovered = hoveredFlight === flight.flightNumber;
                  const hoverColor = getHoverColor(flight.connectionCategory);

                  const rowStyle = {
                    ...baseStyle,
                    backgroundColor: isHovered ? hoverColor : baseStyle.backgroundColor,
                    cursor: 'default'
                  };

                  // No border on sub-rows if multi-dest
                  if (flight.isMultiDestination && !flight.isFirstDestination) {
                    rowStyle.borderTop = 'none';
                  }

                  const rowEvents = {
                    onMouseEnter: () => setHoveredFlight(flight.flightNumber),
                    onMouseLeave: () => setHoveredFlight(null)
                  };

                  return (
                    <TableRow key={`${flight.flightNumber}-${idx}`} sx={rowStyle} {...rowEvents}>
                      {/* Flight # (only if first row in multi-dest) */}
                      {(!flight.isMultiDestination || flight.isFirstDestination) && (
                        <TableCell
                          rowSpan={flight.totalDestinations || 1}
                          sx={{ color: '#fff', fontWeight: flight.isOvernight ? 'bold' : 'normal' }}
                        >
                          {flight.flightNumber}
                          {/* If you had an "Overnight" marker or "Transfer," display them here. */}
                          {flight.isOvernight && (
                            <Typography
                              component="span"
                              sx={{ ml: 1, color: '#F9B612', fontWeight: 'bold', fontSize: '0.8rem' }}
                            >
                              (Overnight)
                            </Typography>
                          )}
                        </TableCell>
                      )}

                      {/* Gate (only if first row in multi-dest) */}
                      {(!flight.isMultiDestination || flight.isFirstDestination) && (
                        <TableCell
                          rowSpan={flight.totalDestinations || 1}
                          sx={{ color: '#fff' }}
                        >
                          {flight.gate}
                        </TableCell>
                      )}

                      {/* ETD (only if first row in multi-dest) */}
                      {(!flight.isMultiDestination || flight.isFirstDestination) && (
                        <TableCell
                          rowSpan={flight.totalDestinations || 1}
                          sx={{ color: '#fff' }}
                        >
                          {flight.est}
                        </TableCell>
                      )}

                      {/* Connection (only if first row in multi-dest) */}
                      {(!flight.isMultiDestination || flight.isFirstDestination) && (
                        <TableCell
                          rowSpan={flight.totalDestinations || 1}
                          sx={{
                            color: flight.connectionTime < 0 
                              ? (params.isPriorityFlight ? 'rgba(249, 182, 18, 0.9)' : 'rgba(255,0,0,0.9)')
                              : '#fff',
                            fontWeight: (flight.connectionTime < 0 || flight.isOvernight) ? 'bold' : 'normal'
                          }}
                        >
                          {isIcelandAirFlight(flight.flightNumber) ? '' : formatConnectionTime(flight.connectionTime)}
                          {flight.isOvernight && (
                            <Typography
                              component="span"
                              sx={{ display: 'block', color: '#F9B612', fontWeight: 'bold', fontSize: '0.75rem' }}
                            >
                              Next Day
                            </Typography>
                          )}
                        </TableCell>
                      )}

                      {/* Destination for sub-rows */}
                      <TableCell sx={{ color: '#fff' }}>
                        {flight.dest}
                      </TableCell>

                      {/* Bags */}
                      <TableCell sx={{ color: '#fff' }}>
                        {flight.bags || 0}
                      </TableCell>

                      {/* Freight */}
                      <TableCell sx={{ color: '#fff' }}>
                        {flight.freight > 0 ? flight.freight : ''}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Terminating row */}
                {(terminating.totalBags || 0) > 0 && (
                  <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} colSpan={6}>
                      Terminating Bags
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {terminating.totalBags}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default BinAccordion;